// Shared calibration-sidecar discovery + parsing for all loaders.
//
// Loaders call `loadCalibrationFromFiles(files, excludeBaseNames, excludePattern)`.
// The exclude args let each loader skip its own required JSONs (predictions,
// picks, etc.) so the first remaining .json is treated as the calibration.

import type { CalibrationData } from '../../types';
import { bundleHasDs, parseDsBundle, type RawDsBundle } from './parse_ds';
import { parseEgoCapture21 } from './parse_kb';
import { parseBundleFormat } from './parse_ocam';

interface CalibrationEntry {
  calibration_json?: unknown;
  calibration_text?: string;
  calibration_time?: string;
}

function unwrapJson(v: unknown): unknown {
  let out = typeof v === 'string' ? JSON.parse(v) : v;
  if (out && typeof out === 'object' && 'calibration_json' in out) {
    const inner = (out as { calibration_json: unknown }).calibration_json;
    out = typeof inner === 'string' ? JSON.parse(inner) : inner;
  }
  return out;
}

function entryHasBundle(entry: CalibrationEntry): boolean {
  try {
    let v: unknown = entry.calibration_json;
    if (
      v == null &&
      typeof entry.calibration_text === 'string' &&
      entry.calibration_text.trim().startsWith('{')
    ) {
      v = entry.calibration_text;
    }
    const unwrapped = unwrapJson(v);
    return (
      !!unwrapped &&
      typeof unwrapped === 'object' &&
      'cameras' in unwrapped &&
      'stereo_pairs' in unwrapped
    );
  } catch {
    return false;
  }
}

function entryHasDs(entry: CalibrationEntry): boolean {
  return (
    typeof entry.calibration_text === 'string' && entry.calibration_text.includes('model_type: ds')
  );
}

function newestCalibrationKind(entries: CalibrationEntry[]): 'bundle' | 'ds' | null {
  const sorted = [...entries].sort((a, b) =>
    (b.calibration_time ?? '').localeCompare(a.calibration_time ?? '')
  );
  for (const entry of sorted) {
    if (entryHasBundle(entry)) return 'bundle';
    if (entryHasDs(entry)) return 'ds';
  }
  return null;
}

// Detect format by top-level keys and delegate.
//   - EgoCapture21.json (KB4): top-level K + R_rel arrays.
//   - DS bundle: top-level `calibrations` with at least one entry whose
//     `calibration_text` YAML carries `model_type: ds`.
//   - egocapture_calibration_bundle/v1 (Scaramuzza): top-level `calibrations` array.
export async function parseCalibrationBlob(blob: Blob): Promise<CalibrationData> {
  const text = await blob.text();
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error('Calibration file is not valid JSON');
  }

  const raw = parsed as Record<string, unknown>;
  if (Array.isArray(raw['K']) && Array.isArray(raw['R_rel'])) {
    return parseEgoCapture21(raw as never);
  }
  if (Array.isArray(raw['calibrations'])) {
    // Mixed EgoCapture2 files may contain older DS entries plus newer bundle
    // entries. Route by the newest recognizable calibration entry, not by the
    // first model type found in the wrapper.
    const kind = newestCalibrationKind(raw['calibrations'] as CalibrationEntry[]);
    if (kind === 'ds' || (kind === null && bundleHasDs(raw))) {
      return parseDsBundle(raw as unknown as RawDsBundle);
    }
    return parseBundleFormat(raw as never);
  }

  throw new Error(
    'Calibration JSON is not in a recognized format (expected EgoCapture21 or bundle format)'
  );
}

function relPath(f: File): string {
  const rel = (f as File & { webkitRelativePath?: string }).webkitRelativePath;
  return rel && rel.length > 0 ? rel : f.name;
}

function baseName(f: File): string {
  const rel = relPath(f);
  return (rel.split('/').pop() ?? f.name).toLowerCase();
}

function candidateCalibrationFiles(
  files: File[],
  excludeBaseNames: ReadonlySet<string>,
  excludePattern?: RegExp
): File[] {
  const excluded = new Set([...excludeBaseNames].map((n) => n.toLowerCase()));
  return files.filter((f) => {
    const base = baseName(f);
    if (!base.endsWith('.json')) return false;
    if (excluded.has(base)) return false;
    if (excludePattern && excludePattern.test(base)) return false;
    return true;
  });
}

export function extractCalibrationFile(
  files: File[],
  excludeBaseNames: ReadonlySet<string> = new Set(),
  excludePattern?: RegExp
): File | null {
  return candidateCalibrationFiles(files, excludeBaseNames, excludePattern)[0] ?? null;
}

export async function loadCalibrationFromFiles(
  files: File[],
  excludeBaseNames: ReadonlySet<string> = new Set(),
  excludePattern?: RegExp
): Promise<CalibrationData | null> {
  const candidates = candidateCalibrationFiles(files, excludeBaseNames, excludePattern);
  for (const f of candidates) {
    try {
      return await parseCalibrationBlob(f);
    } catch {
      // Wrong-shape JSON — try the next candidate.
    }
  }
  return null;
}
